/**
 * The session-restore DECISION, separated from the React hook that applies it.
 *
 * This is the security-critical part of startup — it decides whether the app
 * shows protected data or the login page — so it is kept pure
 * and injectable, and covered by tests for every startup outcome. The hook
 * (`useSessionRestore`) is a thin wrapper that injects the real dependencies.
 *
 * TWO-PHASE optimistic restore:
 *
 *  1. Hydrate the persisted token. If there is one AND it decodes to a usable
 *     user (`decodeUser`), we render the real app shell IMMEDIATELY from that
 *     JWT — no network wait. The decoded user is a UI convenience only; it never
 *     authorizes anything (the API re-checks every permission), and it is
 *     overwritten as soon as `/auth/me` responds.
 *  2. Validate the session in the BACKGROUND via `/auth/me`. Because the fetch
 *     goes through the shared api-client, an expired access token is refreshed
 *     and retried before we ever see an error, so we only observe the FINAL
 *     outcome: confirmed (fresh user), a terminal 401 (session truly over → log
 *     out + purge), or an unreachable API (keep trusting the token — a transient
 *     outage is not a logout).
 *
 * No token, header or credential is returned or logged from here.
 */

import { ApiException } from "@/lib/api-errors";
import type { CurrentUser } from "@/lib/permissions";

/** A hydrated token pair (only the access token is decoded for the optimistic UI). */
export interface HydratedTokens {
  accessToken: string;
  refreshToken: string | null;
}

/** The decision of the background `/auth/me` validation. */
export type ValidationOutcome =
  | { action: "confirm"; user: CurrentUser } // token accepted (possibly after a refresh)
  | { action: "logout" } // terminal 401 — access AND refresh are gone → session over
  | { action: "keep" }; // API unreachable / 5xx → keep trusting the persisted token

/**
 * Turn a single `/auth/me` attempt into a validation decision. The fetch goes
 * through the api-client, so a recoverable expired-access-token is already
 * refreshed + retried; only the terminal outcome reaches here.
 */
export async function validateSession(
  fetchCurrentUser: () => Promise<CurrentUser>,
): Promise<ValidationOutcome> {
  try {
    const user = await fetchCurrentUser();
    return { action: "confirm", user };
  } catch (err) {
    // Only a genuine 401 means "logged out". Anything else (network error,
    // timeout, 5xx) means "could not ask", NOT "your session ended".
    if (err instanceof ApiException && err.status === 401) return { action: "logout" };
    return { action: "keep" };
  }
}

export interface RunRestoreDeps {
  /** Move any persisted token into memory; returns the pair, or null if none. */
  hydrate: () => Promise<HydratedTokens | null>;
  /** Decode an access token into a UI user for the optimistic shell (UI only). */
  decodeUser: (accessToken: string) => CurrentUser | null;
  /** Validate the token via `/auth/me` (through the api-client). */
  fetchCurrentUser: () => Promise<CurrentUser>;
  /** Drop the persisted + in-memory tokens (terminal auth failure only). */
  clearTokens: () => void;
  /** Purge the current user + private data families from the cache. */
  clearPrivateCaches: () => void;
  /** Seed/replace the current user in the query cache (optimistic, then fresh). */
  seedUser: (user: CurrentUser) => void;
  setAuthenticated: () => void;
  setAnonymous: () => void;
  /** Mark the start/end of the background-validation window (toast-suppression). */
  beginValidation: () => void;
  endValidation: () => void;
  /** False once the effect has been cleaned up — stop applying results. */
  isActive: () => boolean;
  /** Dev-only: record the first-shell timing with a short reason word. */
  mark: (reason: string) => void;
}

/**
 * Drive the whole startup restore. Never returns a value — it APPLIES the
 * decision through the injected callbacks. Always leaves the store in a terminal
 * status (authenticated / anonymous); it never leaves it "checking".
 */
export async function runSessionRestore(deps: RunRestoreDeps): Promise<void> {
  const pair = await deps.hydrate();
  if (!deps.isActive()) return;

  // ── No token → login ────────────────────────────────────────────────────────
  if (!pair) {
    deps.setAnonymous();
    deps.mark("no-token");
    return;
  }

  const optimisticUser = deps.decodeUser(pair.accessToken);

  // ── Token decodes → render the real shell NOW, validate in the background ───
  if (optimisticUser) {
    deps.seedUser(optimisticUser);
    deps.setAuthenticated();
    deps.mark("optimistic");

    deps.beginValidation();
    const outcome = await validateSession(deps.fetchCurrentUser);
    deps.endValidation();
    if (!deps.isActive()) return;

    if (outcome.action === "confirm") {
      deps.seedUser(outcome.user); // overwrite the optimistic user with the fresh one
    } else if (outcome.action === "logout") {
      deps.clearTokens();
      deps.clearPrivateCaches();
      deps.setAnonymous();
    }
    // "keep" → the optimistic session stands (temporary network/5xx failure).
    return;
  }

  // ── Opaque / undecodable token (real JWTs never reach here) ─────────────────
  // Fall back to the blocking validate-then-decide path so we never render a
  // shell without a user we can show.
  deps.beginValidation();
  const outcome = await validateSession(deps.fetchCurrentUser);
  deps.endValidation();
  if (!deps.isActive()) return;

  if (outcome.action === "confirm") {
    deps.seedUser(outcome.user);
    deps.setAuthenticated();
    deps.mark("restored");
  } else if (outcome.action === "logout") {
    deps.clearTokens();
    deps.clearPrivateCaches();
    deps.setAnonymous();
    deps.mark("terminal-401");
  } else {
    // API unreachable and no decodable user to show: still trust the token
    // (a transient outage is not a logout). `useCurrentUser` fetches the real
    // profile once the API recovers.
    deps.setAuthenticated();
    deps.mark("offline-optimistic");
  }
}
